import React, { useEffect } from 'react';
import Sidebar from './Sidebar';
import { useUIStore } from '@/stores/uiStore';
import CommandPalette from '@/components/ui/CommandPalette';
import QuickCapture from '@/components/ui/QuickCapture';
import SyncStatus from '@/components/ui/SyncStatus';

interface AppShellProps {
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const sidebarWidth = useUIStore((s) => s.sidebarWidth);
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen);
  const quickCaptureOpen = useUIStore((s) => s.quickCaptureOpen);
  const setIsMobile = useUIStore((s) => s.setIsMobile);
  const isMobile = useUIStore((s) => s.isMobile);

  // Detect mobile viewport
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [setIsMobile]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K — command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        useUIStore.getState().toggleCommandPalette();
      }
      // ⌘\ / Ctrl+\ — toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        useUIStore.getState().toggleSidebar();
      }
      // ⌘N / Ctrl+N — quick capture
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        useUIStore.getState().openQuickCapture();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const effectiveSidebarWidth = sidebarOpen ? sidebarWidth : 0;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-surface-50 dark:bg-surface-950">
      {/* Sidebar */}
      <div
        className="flex-shrink-0 transition-all duration-200 overflow-hidden"
        style={{ width: isMobile ? 0 : effectiveSidebarWidth }}
      >
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
            onClick={() => useUIStore.getState().setSidebarOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed left-0 top-0 bottom-0 z-40 w-64">
            <Sidebar />
          </div>
        </>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-12 flex-shrink-0 flex items-center justify-between px-4 bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800">
          <div className="flex items-center gap-2">
            {/* Hamburger */}
            <button
              type="button"
              onClick={() => useUIStore.getState().toggleSidebar()}
              className="p-1.5 rounded-lg text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-700 dark:hover:text-surface-300 transition-colors"
              aria-label="Toggle sidebar"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <SyncStatus compact />

            {/* Quick search trigger */}
            <button
              type="button"
              onClick={() => useUIStore.getState().openCommandPalette()}
              className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 text-xs text-surface-400 bg-surface-100 dark:bg-surface-800 rounded-lg hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors"
              aria-label="Open command palette"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <kbd className="font-mono">⌘K</kbd>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden relative">
          {children}
        </main>
      </div>

      {/* Command palette portal */}
      {commandPaletteOpen && <CommandPalette />}

      {/* Quick capture portal */}
      {quickCaptureOpen && <QuickCapture />}
    </div>
  );
};

export default AppShell;
